import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItemButton,
  Checkbox,
  Divider,
  Badge,
  Input,
  InputAdornment,
} from "@mui/material";
import { View, ScrollView, TouchableOpacity, Text } from "react-native";
import {
  Close,
  Search,
  Cancel,
  ChevronRight,
  FilterAltOutlined,
  ArrowDropDown,
} from "@mui/icons-material";
import { muiStyles, styles } from "./styles";
interface FilterModalProps {
  filterData: any;
  params: any;
  setFilter: (data: any) => void;
}

const FilterModal: React.FC<FilterModalProps> = ({
  filterData,
  params,
  setFilter,
}) => {
  const filters = filterData?.data || [];
  const [open, setOpen] = useState(false);
  const [selectedFilterIdx, setSelectedFilterIdx] = useState(0);
  const [selectedValues, setSelectedValues] = useState<{
    [key: string]: any[];
  }>({});
  const [search, setSearch] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const validFilterKeys = filters.map((f: any) => f.name);
  const currentFilter = filters[selectedFilterIdx];

  useEffect(() => {
    if (!filterData || !filterData.data) return;
    const initial = buildPrefilledFilters(params, filters);
    setSelectedValues(initial);
  }, [params, filterData]);

  const buildPrefilledFilters = (params: any, filters: any[]) => {
    const prefilled: Record<string, any[]> = {};

    for (const key of validFilterKeys) {
      const values = params[key];
      if (!values) continue;

      const filter = filters.find((f) => f.name === key);
      if (!filter) continue;

      const paramValues = Array.isArray(values)
        ? values.map((v: any) => (isNaN(v) ? v : Number(v)))
        : [];

      if (Array.isArray(filter?.values?.[0])) {
        const matched = filter?.values
          .filter(([id]: any) => paramValues.includes(id))
          .map(([id]: any) => id);
        prefilled[key] = matched;
      } else {
        prefilled[key] = paramValues;
      }
    }
    return prefilled;
  };

  const filteredValues = useMemo(() => {
    if (!currentFilter) return [];
    const values = currentFilter.values;
    if (!search) return values;
    return values.filter((v: any) => {
      const label = Array.isArray(v) ? v[1] : v;
      return label.toLowerCase().includes(search.toLowerCase());
    });
  }, [currentFilter, search]);

  const selectedCount = Object.values(selectedValues).reduce(
    (acc, arr) => acc + (arr?.length || 0),
    0
  );

  const appliedCount = validFilterKeys.reduce((acc, key) => {
    const values = params[key];
    if (Array.isArray(values)) {
      return acc + values.length;
    }
    return acc;
  }, 0);

  const getFilterSelectedCount = (filterName: string) =>
    selectedValues[filterName]?.length || 0;

  const handleCheckboxChange = (filterName: string, value: any) => {
    setSelectedValues((prev) => {
      const prevArr = prev[filterName] || [];
      if (prevArr.includes(value)) {
        return {
          ...prev,
          [filterName]: prevArr.filter((v: any) => v !== value),
        };
      } else {
        return { ...prev, [filterName]: [...prevArr, value] };
      }
    });
  };

  const handleApply = () => {
    setFilter(selectedValues);
    setOpen(false);
  };

  const handleRemoveAll = () => {
    const newParams = { ...params };

    validFilterKeys.forEach((key) => {
      newParams[key] = undefined;
    });

    setFilter(newParams);
    setSelectedValues({});
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedValues({});
    const initial = buildPrefilledFilters(params, filters);
    setSelectedValues(initial);
  };
  const handleSearchClear = () => setSearch("");

  const handleFilterCategoryClick = (idx: number) => {
    setSelectedFilterIdx(idx);
    setSearch("");
  };

  if(!filters || filters.length === 0) return <></>;

  return (
    <View>
      <Box sx={muiStyles.outerContainer}>
        <Button
          variant="outlined"
          onClick={() => setOpen(true)}
          sx={{
            ...muiStyles.filterButton,
            backgroundColor: appliedCount > 0 ? "#F7F2FA" : "#fff",
          }}
        >
          <FilterAltOutlined sx={muiStyles.filterIcon} />
          <Text>Filters</Text>
          {appliedCount > 0 ? (
            <Badge
              color="primary"
              badgeContent={appliedCount}
              sx={{ marginX: 2 }}
            />
          ) : null}
          <ArrowDropDown sx={muiStyles.dropdownIcon} />
        </Button>
      </Box>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="filter-modal-title"
        aria-describedby="filter-modal-description"
        sx={muiStyles.filterModal}
      >
        <Box sx={muiStyles.modalBox}>
          {/* Header */}
          <Box sx={muiStyles.headerContainer}>
            <Typography sx={muiStyles.headerTitle}>All Filters</Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
          <Divider />
          {/* Body */}
          <Box sx={muiStyles.bodyContainer}>
            {/* Left: Filter categories */}
            <Box sx={muiStyles.filterListContainer}>
              <List>
                {filters.map((f: any, idx: number) => (
                  <ListItemButton
                    key={f.name}
                    onClick={() => handleFilterCategoryClick(idx)}
                    sx={{
                      ...muiStyles.filterItem,
                      fontWeight: selectedFilterIdx === idx ? 500 : 400,
                    }}
                  >
                    <Typography
                      sx={{
                        color:
                          selectedFilterIdx === idx ? "#6300A3" : "#555555",
                      }}
                    >
                      {f.display_name}
                    </Typography>
                    {getFilterSelectedCount(f.name) > 0 && (
                      <Badge
                        color="primary"
                        badgeContent={getFilterSelectedCount(f.name)}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </ListItemButton>
                ))}
              </List>
            </Box>
            {/* Right: Filter values */}
            <Box sx={muiStyles.filterValueContainer}>
              <Input
                disableUnderline
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                startAdornment={
                  !isFocused && !search.length ? (
                    <InputAdornment position="start">
                      <IconButton>
                        <Search />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
                endAdornment={
                  search ? (
                    <InputAdornment position="end">
                      <IconButton onClick={handleSearchClear}>
                        <Cancel />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
                placeholder={`Search ${currentFilter?.display_name || ""}`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  backgroundColor:
                    !search.length && !isFocused ? "#F6F6F6" : "transparent",
                  border:
                    !search.length && !isFocused ? "none" : "1px solid #F6F6F6",
                  mb: 2,
                  borderRadius: 1,
                  padding: 1,
                  "&.Mui-focused": {
                    backgroundColor: "transparent",
                    border: "1px solid #F6F6F6",
                  },
                }}
              />
              <ScrollView
                style={{ flex: 1 }}
                contentContainerStyle={styles.filterListContainer}
              >
                {filteredValues.length === 0 ? (
                  <Typography style={muiStyles.noFilterText}>
                    No filters found.
                  </Typography>
                ) : (
                  filteredValues.map((item: any, idx: number) => {
                    const value = Array.isArray(item) ? item[0] : item;
                    const label = Array.isArray(item) ? item[1] : item;
                    return (
                      <TouchableOpacity
                        key={value}
                        style={styles.filterItem}
                        onPress={() =>
                          handleCheckboxChange(currentFilter.name, value)
                        }
                      >
                        <Checkbox
                          checked={
                            selectedValues[currentFilter.name]?.includes(
                              value
                            ) || false
                          }
                          value={value}
                          name={currentFilter.name}
                        />
                        <Typography style={styles.filterLabel}>
                          {label}
                        </Typography>
                      </TouchableOpacity>
                    );
                  })
                )}
              </ScrollView>
              <Box sx={muiStyles.filterModalActions}>
                {appliedCount > 0 ? (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleRemoveAll}
                    sx={muiStyles.removeAllButton}
                  >
                    <Text style={{ color: "#CD2C39" }}>Remove All</Text>
                  </Button>
                ) : null}
                <Button
                  variant="contained"
                  color="primary"
                  disabled={selectedCount === 0}
                  onClick={handleApply}
                  sx={muiStyles.applyButton}
                >
                  <Text style={styles.applyFilterLabel}>
                    {selectedCount === 0
                      ? "Apply Filter"
                      : `Apply ${selectedCount} Filter${selectedCount > 1 ? "s" : ""}`}
                  </Text>
                  <ChevronRight
                    style={{ marginLeft: 8, color: "#fff" }}
                    color="inherit"
                  />
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
    </View>
  );
};

export default FilterModal;
